import { useEffect, useRef, useState } from 'react'

const ANIMATION_MS_PER_PERCENT = 25
const supportsAnimatedCustomProperties = typeof CSS !== 'undefined' && 'registerProperty' in CSS

if (supportsAnimatedCustomProperties) {
  const properties = [
    {name: '--quota-used', syntax: '<percentage>', initialValue: '0%'},
    {name: '--quota-rotation', syntax: '<angle>', initialValue: '0deg'},
  ]

  for (const property of properties) {
    try {
      CSS.registerProperty({...property, inherits: true})
    } catch {
      // Properties can already be registered after a hot reload.
    }
  }
}

export const useAnimatedPercentage = (target:number, immediate = false) => {
  const clampedTarget = Number.isFinite(target) ? Math.min(100, Math.max(0, target)) : 0
  const prefersReducedMotion = typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  const shouldAnimate = supportsAnimatedCustomProperties && !immediate && !prefersReducedMotion
  const initialValue = shouldAnimate ? 0 : clampedTarget
  const previousValue = useRef(initialValue)
  const [animation, setAnimation] = useState({
    value: initialValue,
    duration: shouldAnimate ? Math.abs(clampedTarget - initialValue) * ANIMATION_MS_PER_PERCENT : 0,
  })

  useEffect(() => {
    if (!shouldAnimate) {
      previousValue.current = clampedTarget
      setAnimation({value: clampedTarget, duration: 0})
      return
    }

    const duration = Math.abs(clampedTarget - previousValue.current) * ANIMATION_MS_PER_PERCENT
    const animationFrame = window.requestAnimationFrame(() => {
      previousValue.current = clampedTarget
      setAnimation({value: clampedTarget, duration})
    })

    return () => window.cancelAnimationFrame(animationFrame)
  }, [clampedTarget, shouldAnimate])

  return {
    value: animation.value,
    duration: shouldAnimate ? `${animation.duration}ms` : '0ms',
  }
}
